import React from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Alert,
  AlertTitle,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import { FlyoutErrorBoundaryProps, FlyoutErrorBoundaryState } from '../types';
import {
  FLYOUT_WIDTH,
  FLYOUT_CONTENT_PADDING_X,
  FLYOUT_CONTENT_PADDING_Y,
} from '../constants';

class FlyoutErrorBoundary extends React.Component<
  FlyoutErrorBoundaryProps,
  FlyoutErrorBoundaryState
> {
  constructor(props: FlyoutErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): FlyoutErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('FlyoutErrorBoundary caught an error:', error, errorInfo);

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <FallbackComponent
            error={this.state.error!}
            resetError={this.handleReset}
          />
        );
      }

      // Default error UI
      return (
        <Box sx={{ width: FLYOUT_WIDTH, p: FLYOUT_CONTENT_PADDING_X }}>
          <Stack
            spacing={3}
            alignItems='center'
            sx={{ py: FLYOUT_CONTENT_PADDING_Y }}
          >
            <ErrorIcon
              color='error'
              sx={{ fontSize: 48 }}
            />

            <Alert
              severity='error'
              sx={{ width: '100%' }}
            >
              <AlertTitle>Something went wrong</AlertTitle>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mb: 2 }}
              >
                {this.state.error?.message ||
                  'An unexpected error occurred while loading this flyout.'}
              </Typography>

              {process.env.NODE_ENV === 'development' && (
                <Box
                  component='pre'
                  sx={{
                    fontSize: '12px',
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    p: 1,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '200px',
                    mt: 1,
                  }}
                >
                  {this.state.error?.stack}
                </Box>
              )}
            </Alert>

            <Stack
              direction='row'
              spacing={2}
            >
              <Button
                variant='contained'
                startIcon={<RefreshIcon />}
                onClick={this.handleReset}
                size='small'
              >
                Try Again
              </Button>
            </Stack>
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default FlyoutErrorBoundary;
